import { useRequiredContext } from "@redotech/react-util/context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { ChatVideoLink } from "@redotech/redo-web/ai/chat/components/chat-video-link";
import type { MouseEvent, ReactNode } from "react";
import { useNavigate } from "react-router-dom";

export const CUSTOM_ANALYTICS_REPORT_LINK_PREFIX =
  "/order-management/analytics/reports/custom/";
const CUSTOM_ANALYTICS_REPORT_BARE_LINK_REGEX =
  /(?:https?:\/\/[^\s<>()]+)?\/order-management\/analytics\/reports\/custom\/crpt_[A-Za-z0-9]+/g;

function getCustomAnalyticsReportIdFromLink(href: string): string | null {
  let path = href;

  try {
    path = new URL(href).pathname;
  } catch {
    path = href;
  }

  if (!path.startsWith(CUSTOM_ANALYTICS_REPORT_LINK_PREFIX)) {
    return null;
  }

  return path.slice(CUSTOM_ANALYTICS_REPORT_LINK_PREFIX.length) || null;
}

function shouldLinkifyCustomAnalyticsReportPath({
  markdown,
  offset,
}: {
  markdown: string;
  offset: number;
}): boolean {
  const previousCharacter = markdown[offset - 1];

  if (!previousCharacter) {
    return true;
  }

  if (markdown.slice(offset - 2, offset) === "](") {
    return false;
  }

  if (previousCharacter === "<") {
    return false;
  }

  return /[\s([{:;"']/.test(previousCharacter);
}

export function linkifyCustomAnalyticsReportUrls(markdown: string): string {
  return markdown.replace(
    CUSTOM_ANALYTICS_REPORT_BARE_LINK_REGEX,
    (href: string, offset: number, input: string) => {
      if (
        !shouldLinkifyCustomAnalyticsReportPath({ markdown: input, offset })
      ) {
        return href;
      }

      return `[View custom report](${href})`;
    },
  );
}

export function CustomAnalyticsReportMarkdownLink({
  href,
  children,
}: {
  href: string;
  children?: ReactNode;
}) {
  const navigate = useNavigate();
  const routes = useRequiredContext(MerchantRoutesContext);
  const customReportId = getCustomAnalyticsReportIdFromLink(href);
  const customReportHref = customReportId
    ? `${routes.analyticsReportsHref}/custom/${customReportId}`
    : null;

  if (!customReportId) {
    return <ChatVideoLink href={href}>{children}</ChatVideoLink>;
  }

  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.altKey ||
      event.ctrlKey ||
      event.shiftKey ||
      !customReportHref
    ) {
      return;
    }

    event.preventDefault();
    navigate(customReportHref);
  }

  return (
    <a href={customReportHref ?? href} onClick={handleClick}>
      {children || "View custom report"}
    </a>
  );
}
